/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState, } from "react";
import moment, { Moment, } from "moment";
import cn from "./helpers/cn";

type DateInput = Date | Moment | string | null | undefined;

interface KeyboardDatePickerProps {
  label?: React.ReactNode;
  value?: DateInput;
  onChange?: (date: Date | null, value?: string | null) => void;
  format?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  disableFuture?: boolean;
  disablePast?: boolean;
  minDate?: DateInput;
  maxDate?: DateInput;
  autoOk?: boolean;
  className?: string;
  style?: React.CSSProperties;
  color?: "primary" | "secondary";
  placeholder?: string;
  id?: string;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  InputLabelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  KeyboardButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
}

const WEEK_DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const toMoment = (value: DateInput, format?: string): Moment | null => {
  if (value === null || value === undefined || value === "") return null;
  const m = typeof value === "string"
    ? moment(value, format, true).isValid() ? moment(value, format, true) : moment(value)
    : moment(value);

  return m.isValid() ? m : null;
};

const KeyboardDatePicker = React.forwardRef<HTMLInputElement, KeyboardDatePickerProps>((props, ref) => {
  const {
    label,
    value,
    onChange,
    format = "DD/MM/YYYY",
    fullWidth = false,
    disabled = false,
    required = false,
    error = false,
    helperText,
    disableFuture = false,
    disablePast = false,
    minDate,
    maxDate,
    autoOk = false,
    className,
    style,
    color = "primary",
    placeholder,
    id,
    name,
    onBlur,
    InputLabelProps,
    inputProps,
    KeyboardButtonProps,
  } = props;

  // moment usa "DD/MM/YYYY" — aceitamos o "dd/MM/yyyy" do MUI também
  const normalizedFormat = useMemo(
    () => format.replace(/dd/g, "DD").replace(/yyyy/g, "YYYY"),
    [format]
  );

  const selected = useMemo(() => toMoment(value, normalizedFormat), [value, normalizedFormat]);
  const minM = useMemo(() => toMoment(minDate, normalizedFormat), [minDate, normalizedFormat]);
  const maxM = useMemo(() => toMoment(maxDate, normalizedFormat), [maxDate, normalizedFormat]);

  const [inputValue, setInputValue] = useState<string>(selected ? selected.format(normalizedFormat) : "");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [viewMonth, setViewMonth] = useState<Moment>(selected ? selected.clone().startOf("month") : moment().startOf("month"));

  const containerRef = useRef<HTMLDivElement>(null);

  const hasTime = normalizedFormat.includes("HH");

  useEffect(() => {
    setInputValue(selected ? selected.format(normalizedFormat) : "");
    if (selected) setViewMonth(selected.clone().startOf("month"));
  }, [selected, normalizedFormat]);

  useEffect(() => {
    if (!open) return;

    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);

    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const isDisabledDate = (m: Moment): boolean => {
    if (disableFuture && m.isAfter(moment(), "day")) return true;
    if (disablePast && m.isBefore(moment(), "day")) return true;
    if (minM && m.isBefore(minM, "day")) return true;
    if (maxM && m.isAfter(maxM, "day")) return true;

    return false;
  };

  const emitChange = (m: Moment | null) => {
    if (!onChange) return;
    onChange(m ? m.toDate() : null, m ? m.format(normalizedFormat) : null);
  };
  const applyDateMask = (digits: string) => {
    const value = digits.slice(0, 8);

    const parts = [
      value.slice(0, 2),
      value.slice(2, 4),
      value.slice(4, 8),
    ].filter(Boolean);

    return parts.join("/");
  };

  const applyTimeMask = (digits: string) => {
    const value = digits.slice(0, 4);

    let hour = value.slice(0, 2);
    let minute = value.slice(2, 4);

    if (hour.length === 2) {
      hour = Math.min(Number(hour), 23).toString().padStart(2, "0");
    }

    if (minute.length === 2) {
      minute = Math.min(Number(minute), 59).toString().padStart(2, "0");
    }

    return [hour, minute].filter(Boolean).join(":");
  };

  const applyDateTimeMask = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 12);

    const date = applyDateMask(digits.slice(0, 8));
    const time = applyTimeMask(digits.slice(8));

    if (!time) {
      return date;
    }

    return `${date} ${time}`;
  };

  const applyMask = (raw: string) => {
    if (hasTime) {
      return applyDateTimeMask(raw);
    }

    const digits = raw.replace(/\D/g, "");

    return applyDateMask(digits);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyMask(e.target.value);

    setInputValue(masked);

    const parsed = moment(masked, normalizedFormat, true);

    if (parsed.isValid() && !isDisabledDate(parsed)) {
      setViewMonth(parsed.clone().startOf("month"));
      emitChange(parsed);
    } else if (masked === "") {
      emitChange(null);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    const parsed = moment(inputValue, normalizedFormat, true);

    if (inputValue && (!parsed.isValid() || isDisabledDate(parsed))) {
      emitChange(parsed.isValid() ? parsed : null);
    }
    if (onBlur) onBlur(e);
  };

  const handleSelectDay = (day: Moment) => {
    if (isDisabledDate(day)) {
      return;
    }

    if (selected && hasTime) {
      day
        .hour(selected.hour())
        .minute(selected.minute());
    }

    setInputValue(day.format(normalizedFormat));
    emitChange(day);

    if (autoOk) {
      setOpen(false);
    }
  };

  const calendarDays = useMemo(() => {
    const start = viewMonth.clone().startOf("month").startOf("week");
    const end = viewMonth.clone().endOf("month").endOf("week");
    const days: Moment[] = [];
    const cursor = start.clone();

    while (cursor.isSameOrBefore(end, "day")) {
      days.push(cursor.clone());
      cursor.add(1, "day");
    }

    return days;
  }, [viewMonth]);

  const borderClasses = error
    ? "border-red-600 ring-red-600"
    : focused
      ? color === "primary" ? "border-primary ring-primary ring-1" : "border-secondary ring-secondary ring-1"
      : "border-slate-300 hover:border-slate-900";

  const labelColor = error
    ? "text-red-600"
    : disabled
      ? "text-slate-400"
      : focused
        ? color === "primary" ? "text-primary" : "text-secondary"
        : "text-slate-500";

  return (
    <div
      ref={containerRef}
      style={style}
      className={cn("inline-flex flex-col relative", fullWidth && "w-full", className)}
    >
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "block text-sm font-medium mb-1.5 ml-1 transition-colors duration-200",
            labelColor
          )}
          {...InputLabelProps}
        >
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}

      <div className={cn(
        "relative flex items-center bg-white border rounded transition-all duration-200",
        borderClasses,
        disabled && "bg-slate-50 border-slate-200 cursor-not-allowed"
      )}>
        <input
          ref={ref}
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          disabled={disabled}
          required={required}
          value={inputValue}
          placeholder={placeholder ?? normalizedFormat.toLowerCase()}
          onFocus={() => setFocused(true)}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          className={cn(
            "w-full px-3 py-2.5 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 rounded",
            disabled && "text-slate-400 cursor-not-allowed"
          )}
          {...inputProps}
        />

        <button
          type="button"
          aria-label="change date"
          disabled={disabled}
          onClick={() => !disabled && setOpen((v) => !v)}
          className={cn(
            "shrink-0 p-1.5 mr-1 rounded-full transition-colors",
            disabled ? "text-slate-300 cursor-not-allowed" : "text-slate-500 hover:bg-slate-100"
          )}
          {...KeyboardButtonProps}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </button>
      </div>

      {helperText && (
        <p className={cn(
          "mt-1.5 text-xs ml-3.5",
          error ? "text-red-600" : "secondary-tx",
          disabled && "text-slate-400"
        )}>
          {helperText}
        </p>
      )}

      {open && !disabled && (
        <div className="absolute z-50 top-full mt-2 right-0 bg-white border border-slate-200 rounded-lg shadow-xl p-3 w-72 animate-modal-in">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setViewMonth((m) => m.clone().subtract(1, "month"))}
              className="p-1 rounded hover:bg-slate-100 text-slate-600"
              aria-label="previous month"
            >
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>

            <div className="text-sm font-semibold text-slate-900 capitalize">
              {viewMonth.format("MMMM YYYY")}
            </div>

            <button
              type="button"
              onClick={() => setViewMonth((m) => m.clone().add(1, "month"))}
              className="p-1 rounded hover:bg-slate-100 text-slate-600"
              aria-label="next month"
            >
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {WEEK_DAYS.map((d) => (
              <div key={d} className="text-[11px] text-slate-500 text-center font-medium py-1">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {calendarDays.map((day) => {
              const isCurrentMonth = day.isSame(viewMonth, "month");
              const isSelected = selected ? day.isSame(selected, "day") : false;
              const isToday = day.isSame(moment(), "day");
              const disabledDay = isDisabledDate(day);

              return (
                <button
                  key={day.format("YYYY-MM-DD")}
                  type="button"
                  disabled={disabledDay}
                  onClick={() => handleSelectDay(day)}
                  className={cn(
                    "text-xs h-8 w-8 mx-auto flex items-center justify-center rounded-full transition-colors",
                    !isCurrentMonth && "text-slate-300",
                    isCurrentMonth && !isSelected && !disabledDay && "text-slate-800 hover:bg-slate-100",
                    isToday && !isSelected && "ring-1 ring-primary",
                    isSelected && (color === "primary" ? "bg-primary text-contrast-text-primary" : "bg-secondary text-contrast-text-secondary"),
                    disabledDay && "text-slate-300 cursor-not-allowed line-through"
                  )}
                >
                  {day.date()}
                </button>
              );
            })}
          </div>

          {!autoOk && (
            <div className="flex justify-end gap-2 mt-3 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => { emitChange(null); setInputValue(""); setOpen(false); }}
                className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1"
              >
                Limpar
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={cn(
                  "text-xs px-3 py-1 rounded font-semibold",
                  color === "primary" ? "text-primary hover:bg-primary/10" : "text-secondary hover:bg-secondary/10"
                )}
              >
                OK
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

KeyboardDatePicker.displayName = "KeyboardDatePicker";

export default KeyboardDatePicker;
